import React, { useState, useEffect } from 'react';

interface FormData {
  nome: string;
  email: string;
  cpf: string;
  telefone: string;
}

const Estacionamento2Page: React.FC = () => {
  // Redirecionamento automático para o site externo
  useEffect(() => {
    window.location.href = 'https://estacionamento.centrodacoroa.com.br/consultar';
  }, []);

  const [formData, setFormData] = useState<FormData>({
    nome: '',
    email: '',
    cpf: '',
    telefone: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormData, string>>
  >({});

  const validateCPF = (cpf: string): boolean => {
    const cleanCPF = cpf.replace(/[^\d]/g, '');
    return cleanCPF.length === 11;
  };

  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[^\d]/g, '');
    return cleanPhone.length === 10 || cleanPhone.length === 11;
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.nome || formData.nome.length < 3) {
      newErrors.nome = 'Nome deve ter pelo menos 3 caracteres';
    }

    if (!formData.email || !validateEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.cpf || !validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF deve ter 11 dígitos';
    }

    if (!formData.telefone || !validatePhone(formData.telefone)) {
      newErrors.telefone = 'Telefone deve ter 10 ou 11 dígitos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Limpar erro do campo quando usuário começar a digitar
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const formatCPF = (value: string) => {
    const cleanValue = value.replace(/[^\d]/g, '');
    return cleanValue.replace(
      /(\d{3})(\d{3})(\d{3})(\d{2})/,
      '$1.$2.$3-$4'
    );
  };

  const formatPhone = (value: string) => {
    const cleanValue = value.replace(/[^\d]/g, '');
    if (cleanValue.length <= 10) {
      return cleanValue.replace(
        /(\d{2})(\d{4})(\d{4})/,
        '($1) $2-$3'
      );
    }
    return cleanValue.replace(
      /(\d{2})(\d{5})(\d{4})/,
      '($1) $2-$3'
    );
  };

  const handleCPFChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formatted = formatCPF(e.target.value);
    setFormData((prev) => ({ ...prev, cpf: formatted }));
  };

  const handlePhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formatted = formatPhone(e.target.value);
    setFormData((prev) => ({
      ...prev,
      telefone: formatted,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Usar as variáveis de ambiente do Supabase
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Configuração do Supabase não encontrada');
      }
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/criar-pagamento-pagseguro`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseKey}`
          },
          body: JSON.stringify(formData)
        }
      );

      // Verificar se a resposta não está vazia
      const responseText = await response.text();
      
      if (!responseText || responseText.trim() === '') {
        throw new Error('Resposta vazia do servidor');
      }

      // Tentar fazer parse do JSON
      let data: any;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`Resposta inválida do servidor: ${responseText.substring(0, 100)}...`);
      }

      // Verificar se houve erro HTTP
      if (!response.ok) {
        const errorMessage = data?.error || `Erro HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      // Verificar se o pagamento foi criado com sucesso
      if (data.success && data.payment_link) {
        alert('Pagamento criado com sucesso! Você será redirecionado para o PagSeguro.');
        window.location.href = data.payment_link;
      } else {
        const errorMessage = data.error || 'Erro desconhecido ao criar pagamento';
        throw new Error(errorMessage);
      }

    } catch (error: any) {
      let userMessage = 'Erro ao processar pagamento. Tente novamente.';
      
      if (error?.message && typeof error.message === 'string') {
        if (error.message.includes('Failed to fetch')) {
          userMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
        } else if (error.message.includes('NetworkError')) {
          userMessage = 'Erro de rede. Tente novamente em alguns segundos.';
        } else {
          userMessage = error.message;
        }
      }
      
      alert(`Erro: ${userMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center">
      {/* Loading durante o redirecionamento */}
      <div className="text-center">
        <div className="w-16 h-16 bg-green-600 rounded-full mx-auto mb-4 flex items-center justify-center">
          <i className="ri-loader-4-line text-white text-2xl animate-spin"></i>
        </div>
        <h2 className="text-2xl font-bold text-green-800 mb-2">
          Redirecionando...
        </h2>
        <p className="text-green-600">
          Você será redirecionado para o sistema de estacionamento
        </p>
      </div>
    </div>
  );
};

export default Estacionamento2Page;
